import axios, {AxiosResponse} from 'axios'
import baseURLs from '@/api/APIBaseURLs'

/**
 * Single owner of JWT token state and the refresh routine.
 *
 * Storage layout (localStorage):
 *  - access_token: JWT bearer for API calls
 *  - refresh_token: long-lived token used to mint new access tokens
 *  - expires_at: access token expiry as epoch SECONDS
 *
 * IMPORTANT: this module must stay a leaf — do NOT import `@/services/axios` or
 * `@/variables.js`. Either drags a heavy graph (axios instance ->
 * router/generalStore; variables -> utils -> Auth) into every module that only
 * wants a token getter or header builder, creating import cycles. Auth registers
 * its post-refresh hook via `setOnRefreshed()` at module-eval time, so a cycle
 * back through here would run that call while `onRefreshed` is still in its TDZ.
 * The axios instance instead registers a sink via `setTokenSink()` so we can push
 * token changes onto its default header without importing it. The refresh POST
 * uses the raw axios lib so it never re-enters our interceptors.
 */

/** Base request headers. Inlined (not imported from `@/variables.js`) to keep this module a leaf. */
const headers = { 'Content-Type': 'application/json' } as const

export const LS_ACCESS_TOKEN = 'access_token'
export const LS_REFRESH_TOKEN = 'refresh_token'
export const LS_EXPIRES_AT = 'expires_at'

/** Seconds before expiry at which we refresh proactively. */
export const DEFAULT_SKEW_SECONDS = 30

export interface RefreshResult {
  ok: boolean
  accessToken?: string
  /** HTTP status of the failed refresh (0 for network error / no refresh token / stale) */
  status?: number
  response?: AxiosResponse
}

export interface TokenPayload {
  access_token: string
  expiry: number | string
  refresh_token?: string
}

/** Http surface the refresh call needs; lets tests inject a fake via __setHttpClient. */
type RefreshHttpClient = Pick<typeof axios, 'post'>
type RefreshedHook = (response: AxiosResponse) => void
/** Receives the current access token (or null on clear) so axios can sync its default header. */
type TokenSink = (token: string | null) => void

/** Raw http client for the refresh call; overridable in tests. */
let http: RefreshHttpClient = axios
/** In-flight refresh promise, shared by concurrent callers (dedupe). */
let inflight: Promise<RefreshResult> | null = null
/**
 * Bumped whenever tokens are cleared (logout / auth failure). A refresh that
 * started before a clear must not resurrect tokens when it resolves.
 */
let generation = 0
/** Optional hook so Auth can refresh identity/org data after a token refresh. */
let onRefreshed: RefreshedHook | null = null
/** Registered by axios.js to keep its default Authorization header in sync. */
let tokenSink: TokenSink | null = null

export function setOnRefreshed (hook: RefreshedHook | null): void {
  onRefreshed = hook
}

export function setTokenSink (sink: TokenSink | null): void {
  tokenSink = sink
}

function nowSeconds (): number {
  return Math.floor(Date.now() / 1000)
}

export function getAccessToken (): string | null {
  return localStorage.getItem(LS_ACCESS_TOKEN)
}

export function getRefreshToken (): string | null {
  return localStorage.getItem(LS_REFRESH_TOKEN)
}

/**
 * Build request headers carrying the access token. Declared as a hoisted function
 * so it is safe to call during the tokenService<->axios circular init.
 * @param forcedAccessToken use this token instead of the stored one (e.g. a just-refreshed token)
 */
export function getHeadersWithAccessToken (forcedAccessToken: string | null = null): Record<string, string> {
  return { ...headers, Authorization: 'Bearer ' + (forcedAccessToken || getAccessToken()) }
}

/** Build request headers carrying the refresh token (used only for the /refresh call). */
export function getHeadersWithRefreshToken (): Record<string, string> {
  return { ...headers, Authorization: 'Bearer ' + getRefreshToken() }
}

/** Access token expiry in epoch seconds, or NaN when unset/unparseable. */
export function getExpiresAt (): number {
  const raw = localStorage.getItem(LS_EXPIRES_AT)
  return raw === null ? NaN : Number(raw)
}

/**
 * @param skewSeconds treat the token as invalid this many seconds before its
 * real expiry (default 0). Pass a positive skew for proactive refresh.
 */
export function isAccessTokenValid (skewSeconds = 0): boolean {
  const exp = getExpiresAt()
  return !!getAccessToken() && Number.isFinite(exp) && nowSeconds() + skewSeconds < exp
}

/** True when there is a token but it is within `skewSeconds` of (or past) expiry. */
export function isAccessTokenExpiring (skewSeconds = DEFAULT_SKEW_SECONDS): boolean {
  return !!getAccessToken() && !isAccessTokenValid(skewSeconds)
}

/**
 * Persist tokens. `refresh_token` is written only when present, so a `/refresh`
 * response that omits it (backend does not rotate) keeps the existing one.
 */
export function setTokens (payload: TokenPayload): void {
  localStorage.setItem(LS_ACCESS_TOKEN, payload.access_token)
  localStorage.setItem(LS_EXPIRES_AT, String(payload.expiry))
  if (payload.refresh_token) {
    localStorage.setItem(LS_REFRESH_TOKEN, payload.refresh_token)
  }
  tokenSink?.(payload.access_token)
}

/** Drop all token state. Bumps the generation so an in-flight refresh can't resurrect it. */
export function clearTokens (): void {
  invalidateInflight()
  localStorage.removeItem(LS_ACCESS_TOKEN)
  localStorage.removeItem(LS_REFRESH_TOKEN)
  localStorage.removeItem(LS_EXPIRES_AT)
  tokenSink?.(null)
}

/**
 * Invalidate any in-flight refresh without touching storage or the axios header.
 * Used by logout, which needs the current Authorization header to remain set for
 * the logout request while still preventing a concurrent refresh from resurrecting
 * tokens after logout.
 */
export function invalidateInflight (): void {
  generation += 1
  inflight = null
}

async function doRefresh (gen: number): Promise<RefreshResult> {
  if (!getRefreshToken()) {
    return { ok: false, status: 0 }
  }
  try {
    const response = await http.post(baseURLs.manageAPI + '/refresh', {}, { headers: getHeadersWithRefreshToken() })
    // Logged out (or tokens cleared) while this refresh was in flight — discard.
    if (gen !== generation) {
      return { ok: false, status: 0 }
    }
    setTokens({
      access_token: response.data.access_token,
      expiry: response.data.expiry,
      refresh_token: response.data.refresh_token,
    })
    if (onRefreshed) {
      try {
        onRefreshed(response)
      } catch (e) {
        console.error('onRefreshed hook failed', e)
      }
    }
    return { ok: true, accessToken: response.data.access_token, response }
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status ?? 0
    console.error('Failed to refresh token', status, error)
    // Only wipe on auth failure — a transient network error must not log the user out.
    if (gen === generation && (status === 401 || status === 422)) {
      clearTokens()
    }
    return { ok: false, status }
  }
}

/**
 * Refresh the access token. Concurrent calls share a single in-flight request.
 * Never throws — always resolves to a normalized {@link RefreshResult}.
 */
export function refresh (): Promise<RefreshResult> {
  if (inflight) {
    return inflight
  }
  inflight = doRefresh(generation).finally(() => {
    inflight = null
  })
  return inflight
}

/**
 * Proactive guard for the request interceptor: refresh first when the access
 * token is missing/expiring and a refresh token exists. Returns the current
 * token state without a network call when already valid.
 */
export function ensureValidAccessToken (skewSeconds = DEFAULT_SKEW_SECONDS): Promise<RefreshResult> {
  if (!getRefreshToken()) {
    return Promise.resolve({ ok: false, status: 0 })
  }
  if (isAccessTokenValid(skewSeconds)) {
    return Promise.resolve({ ok: true, accessToken: getAccessToken() as string })
  }
  return refresh()
}

/** Test-only seam: override the raw http client used for the refresh POST. */
export function __setHttpClient (client: RefreshHttpClient): void {
  http = client
}

/** Test-only seam: reset module-level dedupe/generation state between tests. */
export function __resetTokenService (): void {
  http = axios
  inflight = null
  generation = 0
  onRefreshed = null
  tokenSink = null
}
